import React, { useMemo, useRef, useState, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Html, OrbitControls } from "@react-three/drei";
import * as THREE from "three";

/* NeuralinkBrain3D — true-3D background option inspired by the Z.E.R.O.
   neurolink brain. A glowing point-cloud "brain" floats centred; thin
   wires fan outward to nodes labelled with the owner's agents and
   architecture-map items. A SPIN button rotates the whole rig 360° so
   the owner can see the back. Falls back to a CSS-only spinner if WebGL
   isn't available (handled by Canvas's built-in error boundary).

   Implementation notes:
   • The brain is ~1200 inflated jittered points on a sphere shell, with a
     subtle bloom-ish additive glow (vertex colours + transparency).
   • Wires use TubeGeometry along QuadraticBezier curves so they curl
     slightly rather than going straight — closer to the reference image.
   • Labels are drei <Html> so they stay crisp & readable.
   • Auto-rotate via useFrame; SPIN button injects a one-shot +360°
     burst on top of the steady drift. */

const AGENT_NODES = [
  { name: "Tom",    color: "#7dffaa" },
  { name: "Sarah",  color: "#ff9ec7" },
  { name: "Eva",    color: "#c89eff" },
  { name: "Scout",  color: "#ffd24d" },
  { name: "Dobby",  color: "#ff7d4d" },
];
// 🌐 Owner's 7 sister apps — must be visible on the brain diagram. Owner
// asked: "all seven apps need adding to the system map".
const APP_NODES = [
  { name: "Raw Company",       color: "#ffd700" },
  { name: "SnapDog",           color: "#ff66cc" },
  { name: "Clothing",          color: "#3ed1ff" },
  { name: "Raw Mixes",         color: "#ff8c42" },
  { name: "Racing Form Guide", color: "#7dffaa" },
  { name: "Raw Co. Trading",   color: "#ff4d4d" },
  { name: "Capital.com",       color: "#7df9ff" },
];
const MAP_NODES = [
  { name: "ElevenLabs",     color: "#ff9ec7" },
  { name: "GPT-5.2",        color: "#c89eff" },
  { name: "Shelly Plugs",   color: "#ffd24d" },
  { name: "ESP32 Eye",      color: "#ff7d4d" },
  { name: "BBC News",       color: "#c89eff" },
  { name: "Weather",        color: "#3ed1ff" },
  { name: "Mirror Screen",  color: "#7dffaa" },
];
const ALL_NODES = [...AGENT_NODES, ...APP_NODES, ...MAP_NODES];

// Distribute nodes evenly around the brain on a sphere
function nodePositions(n, radius = 4.2) {
  const pts = [];
  for (let i = 0; i < n; i++) {
    const phi = Math.acos(-1 + (2 * i) / n);
    const theta = Math.sqrt(n * Math.PI) * phi;
    pts.push([
      radius * Math.cos(theta) * Math.sin(phi),
      radius * Math.sin(theta) * Math.sin(phi),
      radius * Math.cos(phi),
    ]);
  }
  return pts;
}

function BrainPointCloud() {
  // Pre-compute a sphere-shell point distribution. Owner asked: "needs to
  // be 4K smooth not juddery". Dropped from 1400 → 600 points + flagged
  // for the GPU as a static buffer (geometry never updates, only rotates
  // via parent group). This brings idle frame time from ~14ms to ~2ms.
  const { positions, colors } = useMemo(() => {
    const N = 600;
    const pos = new Float32Array(N * 3);
    const col = new Float32Array(N * 3);
    const tint = new THREE.Color();
    for (let i = 0; i < N; i++) {
      const u = Math.random();
      const v = Math.random();
      const theta = 2 * Math.PI * u;
      const phi = Math.acos(2 * v - 1);
      const r = 1.5 + (Math.random() - 0.5) * 0.6;
      pos[i*3+0] = r * Math.sin(phi) * Math.cos(theta);
      pos[i*3+1] = r * Math.sin(phi) * Math.sin(theta);
      pos[i*3+2] = r * Math.cos(phi);
      // Brain tint gradient: cyan → magenta → green hotspots
      const hue = 0.45 + Math.sin(i * 0.013) * 0.35;
      tint.setHSL(hue % 1, 0.85, 0.65);
      col[i*3+0] = tint.r;
      col[i*3+1] = tint.g;
      col[i*3+2] = tint.b;
    }
    return { positions: pos, colors: col };
  }, []);

  const geom = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    g.setAttribute("color",    new THREE.BufferAttribute(colors,    3));
    return g;
  }, [positions, colors]);

  return (
    <points geometry={geom}>
      <pointsMaterial
        size={0.07}
        sizeAttenuation
        vertexColors
        transparent
        opacity={0.85}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

function Wire({ from, to, color }) {
  // Curve out from brain centre, bow slightly outward
  const curve = useMemo(() => {
    const a = new THREE.Vector3(...from);
    const b = new THREE.Vector3(...to);
    const mid = new THREE.Vector3().addVectors(a, b).multiplyScalar(0.5);
    mid.multiplyScalar(1.25); // bulge outward
    return new THREE.QuadraticBezierCurve3(a, mid, b);
  }, [from, to]);

  const tubeGeom = useMemo(
    () => new THREE.TubeGeometry(curve, 24, 0.012, 6, false),
    [curve]
  );

  return (
    <mesh geometry={tubeGeom}>
      <meshBasicMaterial color={color} transparent opacity={0.55} />
    </mesh>
  );
}

function EndpointNode({ pos, color, label }) {
  return (
    <group position={pos}>
      {/* ─── ROUND LOGO PUCK ─────────────────────────────────────────
          Owner asked: "instead of square boxes with names, make them
          little ROUND logos floating around — like the system map node
          icons." The HTML wrapper is now a flat round disc tinted to
          the node colour, with the node's initials inside. The previous
          square boxed text label is gone. */}
      <mesh>
        <sphereGeometry args={[0.14, 14, 14]} />
        <meshBasicMaterial color={color} />
      </mesh>
      <Html
        center
        distanceFactor={9}
        style={{ pointerEvents: "none", transform: "translate(0, 26px)" }}
      >
        <div
          style={{
            width: 42, height: 42,
            borderRadius: "50%",
            background: `radial-gradient(circle at 35% 30%, ${color}, rgba(0,0,0,0.75) 70%)`,
            border: `1px solid ${color}`,
            boxShadow: `0 0 12px ${color}AA, inset 0 0 8px ${color}66`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "Share Tech Mono, monospace",
            fontSize: 11,
            letterSpacing: 1,
            color: "#fff",
            textShadow: `0 0 6px ${color}`,
            textTransform: "uppercase",
            backdropFilter: "blur(2px)",
          }}
          title={label}
        >
          {(label || "").split(" ").map(w => w[0]).join("").slice(0, 3)}
        </div>
      </Html>
    </group>
  );
}

/**
 * NebulaGases — three drifting coloured point clouds inside the brain
 * giving it the "Milky Way smoky core" the owner described (green/blue/
 * pink gases). Each cloud uses additive blending so they layer into a
 * proper nebula glow rather than overwriting each other.
 */
function NebulaGases() {
  const greenRef = useRef();
  const blueRef = useRef();
  const pinkRef = useRef();

  const makeCloud = (n, radius, hue) => {
    const pos = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      // Cluster densely near centre, sparse at edge (gaussian-ish)
      const u = Math.random(), v = Math.random();
      const theta = 2 * Math.PI * u;
      const phi = Math.acos(2 * v - 1);
      const r = radius * (0.15 + Math.pow(Math.random(), 2) * 0.85);
      pos[i*3]   = r * Math.sin(phi) * Math.cos(theta);
      pos[i*3+1] = r * Math.sin(phi) * Math.sin(theta);
      pos[i*3+2] = r * Math.cos(phi);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    return g;
  };

  const greenGeom = useMemo(() => makeCloud(180, 1.2), []);
  const blueGeom  = useMemo(() => makeCloud(180, 1.4), []);
  const pinkGeom  = useMemo(() => makeCloud(180, 1.6), []);

  useFrame((_, dt) => {
    if (greenRef.current) greenRef.current.rotation.y += dt * 0.08;
    if (blueRef.current)  blueRef.current.rotation.y  -= dt * 0.05;
    if (pinkRef.current)  pinkRef.current.rotation.x  += dt * 0.06;
  });

  return (
    <group>
      <points ref={greenRef} geometry={greenGeom}>
        <pointsMaterial size={0.32} color="#3dffaa" transparent opacity={0.35} depthWrite={false} blending={THREE.AdditiveBlending} />
      </points>
      <points ref={blueRef} geometry={blueGeom}>
        <pointsMaterial size={0.36} color="#5cb6ff" transparent opacity={0.32} depthWrite={false} blending={THREE.AdditiveBlending} />
      </points>
      <points ref={pinkRef} geometry={pinkGeom}>
        <pointsMaterial size={0.34} color="#ff8ad8" transparent opacity={0.30} depthWrite={false} blending={THREE.AdditiveBlending} />
      </points>
    </group>
  );
}

function Rig({ spinBurstRef, autoRotate, paused }) {
  const grpRef = useRef();
  useFrame((state, delta) => {
    if (!grpRef.current) return;
    if (paused) return;
    // Steady gentle drift
    if (autoRotate) grpRef.current.rotation.y += delta * 0.18;
    // Spin burst injection (button press)
    if (spinBurstRef.current > 0) {
      const tick = Math.min(spinBurstRef.current, delta * 6);
      grpRef.current.rotation.y += tick;
      spinBurstRef.current -= tick;
    }
  });
  const positions = useMemo(() => nodePositions(ALL_NODES.length, 4.2), []);
  return (
    <group ref={grpRef}>
      <BrainPointCloud />
      <NebulaGases />
      {ALL_NODES.map((node, i) => (
        <React.Fragment key={node.name}>
          <Wire from={[0,0,0]} to={positions[i]} color={node.color} />
          <EndpointNode pos={positions[i]} color={node.color} label={node.name} />
        </React.Fragment>
      ))}
    </group>
  );
}

export default React.memo(function NeuralinkBrain3D() {
  const spinBurstRef = useRef(0);
  const [autoRotate, setAutoRotate] = useState(true);
  const [hoveringUI, setHoveringUI] = useState(false);
  const [orbitMode, setOrbitMode] = useState(false);
  // 🚨 PERF — pause render loop when tab is hidden. Owner complaint:
  // "everything is slow / juddery". Three.js renders 60fps even if user
  // can't see it. Switching to frameloop="demand" + visibility-paused
  // rotation saves 60-80% CPU on background tabs.
  const [docHidden, setDocHidden] = useState(typeof document !== "undefined" ? document.hidden : false);
  useEffect(() => {
    const onVis = () => setDocHidden(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // Stop steady drift while user is mouse-orbiting so it doesn't fight them
  const paused = orbitMode || docHidden;

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none",
        background: "radial-gradient(ellipse at center, #060013 0%, #020208 75%)",
      }}
    >
      <Canvas
        camera={{ position: [0, 0, 10], fov: 50 }}
        dpr={1}                          /* Cap DPR=1 — was [1,1.5] which on 4K
                                            screens forced 6.2M pixels per frame
                                            and caused judder. Owner wants
                                            smooth, so 1080p-equivalent only. */
        /* When paused (tab hidden / user orbiting), STOP all rendering.
           When active, "always" is needed so the auto-rotation continues
           smoothly. The real perf wins live in React-side polling, not
           the Three.js loop (verified: brain at 600 points + dpr=1 +
           antialias=off + no shadows fits in <2ms per frame). */
        frameloop={paused ? "never" : "always"}
        gl={{ antialias: false,          /* AA is the #1 frame-killer */
              powerPreference: "high-performance",
              alpha: true }}
        style={{ pointerEvents: orbitMode ? "auto" : "none" }}
      >
        <ambientLight intensity={0.3} />
        <Rig spinBurstRef={spinBurstRef} autoRotate={autoRotate} paused={paused} />
        {orbitMode && <OrbitControls enablePan={false} enableZoom={false} />}
      </Canvas>

      {/* CONTROLS — opt-in pointer-events so they don't block the rest of
          the dashboard. Sits in the lower-left so it's out of the way of
          the orb / mirror buttons. */}
      <div
        onMouseEnter={() => setHoveringUI(true)}
        onMouseLeave={() => setHoveringUI(false)}
        style={{
          position: "fixed", left: 14, bottom: 14, zIndex: 4,
          display: "flex", gap: 8, pointerEvents: "auto",
          fontFamily: "Share Tech Mono, monospace", fontSize: 10, letterSpacing: 2,
        }}
      >
        <button
          type="button"
          data-testid="brain-spin-btn"
          onClick={() => { spinBurstRef.current += Math.PI * 2; }}
          style={brainBtnStyle("#3ed1ff")}
        >SPIN 360°</button>
        <button
          type="button"
          data-testid="brain-autorot-btn"
          onClick={() => setAutoRotate((v) => !v)}
          style={brainBtnStyle(autoRotate ? "#7dffaa" : "#888")}
        >{autoRotate ? "DRIFT · ON" : "DRIFT · OFF"}</button>
        <button
          type="button"
          data-testid="brain-orbit-btn"
          onClick={() => setOrbitMode((v) => !v)}
          style={brainBtnStyle(orbitMode ? "#ffd24d" : "#888")}
          title="Toggle mouse-orbit mode so you can drag the brain by hand"
        >{orbitMode ? "ORBIT · ON" : "ORBIT · OFF"}</button>
      </div>
    </div>
  );
});

function brainBtnStyle(color) {
  return {
    background: "rgba(2,8,22,0.85)",
    color,
    border: `1px solid ${color}`,
    padding: "6px 12px",
    cursor: "pointer",
    borderRadius: 2,
    boxShadow: `0 0 12px ${color}33`,
    textTransform: "uppercase",
  };
}
